export const baseCustomer = {
	id: "",
	name: "",
	logo: "",
	seasons: [],
};

export const normalizeSeasonForSave = (season) => {
	const payload = {
		name: season.name,
		active: season.active ? 1 : 0,
		initial_date: season.initial_date,
		end_date: season.end_date,
	};

	if (season.id) {
		payload.id = season.id;
	}

	return payload;
};

export const toDateInputValue = (value) => {
	if (!value) {
		return "";
	}

	const trimmedValue = String(value).trim();
	const isDayMonthYearFormat = /^\d{2}-\d{2}-\d{4}$/.test(trimmedValue);

	if (isDayMonthYearFormat) {
		const [day, month, year] = trimmedValue.split("-");
		return `${year}-${month}-${day}`;
	}

	return trimmedValue.slice(0, 10);
};

export const hasDateInputValue = (value) => /^\d{4}-\d{2}-\d{2}$/.test(toDateInputValue(value));

export const validateSeasons = (seasons) => {
	if (!seasons?.length) {
		return { valid: false, message: "Debe existir al menos una temporada." };
	}

	const activeSeasons = seasons.filter((season) => Number(season.active) === 1);
	if (activeSeasons.length !== 1) {
		return { valid: false, message: "Debe haber exactamente una temporada activa." };
	}

	for (const season of seasons) {
		if (!hasDateInputValue(season.initial_date) || !hasDateInputValue(season.end_date)) {
			return { valid: false, message: "Todas las temporadas deben tener fecha de inicio y fin." };
		}

		const normalizedInitialDate = toDateInputValue(season.initial_date);
		const normalizedEndDate = toDateInputValue(season.end_date);

		if (new Date(normalizedEndDate) <= new Date(normalizedInitialDate)) {
			return { valid: false, message: `La fecha de fin debe ser posterior al inicio en "${season.name}".` };
		}
	}

	return { valid: true };
};

export const validateSeasonDates = (initialDate, endDate) => {
	if (!hasDateInputValue(initialDate) || !hasDateInputValue(endDate)) {
		return { valid: false, message: "Debe ingresar fecha de inicio y fin." };
	}

	const normalizedInitialDate = toDateInputValue(initialDate);
	const normalizedEndDate = toDateInputValue(endDate);

	if (new Date(normalizedEndDate) <= new Date(normalizedInitialDate)) {
		return { valid: false, message: "La fecha de fin debe ser posterior a la de inicio." };
	}

	return { valid: true };
};

const parseSeasonDate = (value) => {
	const normalizedValue = toDateInputValue(value);

	if (!normalizedValue) {
		return null;
	}

	const [year, month, day] = normalizedValue.split("-").map(Number);
	return new Date(year, month - 1, day);
};

const diffSeasonDays = (fromDate, toDate) => {
	const from = new Date(fromDate);
	const to = new Date(toDate);
	from.setHours(0, 0, 0, 0);
	to.setHours(0, 0, 0, 0);
	return Math.round((to - from) / (1000 * 60 * 60 * 24));
};

const formatDaysLabel = (days) => (days === 1 ? "1 día" : `${days} días`);

export const getSeasonStatusAlerts = (seasons = []) => {
	const groups = [];

	if (!seasons.length) {
		return [
			{
				type: "error",
				title: "Temporadas",
				messages: ["No hay temporadas configuradas."],
			},
		];
	}

	const activeSeason = seasons.find((season) => Number(season.active) === 1);
	const activeMessages = [];

	if (!activeSeason) {
		activeMessages.push("No hay una temporada marcada como activa.");
	} else if (!hasDateInputValue(activeSeason.initial_date) || !hasDateInputValue(activeSeason.end_date)) {
		activeMessages.push(`La temporada activa "${activeSeason.name}" no tiene fechas completas.`);
	} else {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const activeStart = parseSeasonDate(activeSeason.initial_date);
		const activeEnd = parseSeasonDate(activeSeason.end_date);

		if (today < activeStart) {
			const daysUntil = diffSeasonDays(today, activeStart);
			activeMessages.push(`La temporada activa "${activeSeason.name}" se habilitará en ${formatDaysLabel(daysUntil)}.`);
		} else if (today > activeEnd) {
			const daysSince = diffSeasonDays(activeEnd, today);
			activeMessages.push(`La temporada activa "${activeSeason.name}" finalizó hace ${formatDaysLabel(daysSince)}.`);
		}
	}

	if (activeMessages.length) {
		groups.push({
			type: activeSeason ? "warning" : "error",
			title: "Temporada activa",
			messages: activeMessages,
		});
	}

	const continuityMessages = [];
	const datedSeasons = seasons
		.filter((season) => hasDateInputValue(season.initial_date) && hasDateInputValue(season.end_date))
		.sort((firstSeason, secondSeason) => parseSeasonDate(firstSeason.initial_date) - parseSeasonDate(secondSeason.initial_date));

	for (let index = 0; index < datedSeasons.length - 1; index++) {
		const currentSeason = datedSeasons[index];
		const nextSeason = datedSeasons[index + 1];
		const currentEnd = parseSeasonDate(currentSeason.end_date);
		const nextStart = parseSeasonDate(nextSeason.initial_date);
		const daysBetweenSeasons = diffSeasonDays(currentEnd, nextStart);

		if (daysBetweenSeasons < 1) {
			continuityMessages.push(`Las temporadas "${currentSeason.name}" y "${nextSeason.name}" tienen fechas superpuestas.`);
			continue;
		}

		if (daysBetweenSeasons > 1) {
			const gapDays = daysBetweenSeasons - 1;
			continuityMessages.push(
				`Hay un salto de ${formatDaysLabel(gapDays)} entre "${currentSeason.name}" y "${nextSeason.name}". Las temporadas deben ser continuas.`,
			);
		}
	}

	if (continuityMessages.length) {
		groups.push({
			type: "warning",
			title: "Continuidad de temporadas",
			messages: continuityMessages,
		});
	}

	return groups;
};
